import React from "react";
import {
  Pill,
  Eye,
  Settings,
  Image,
  Stethoscope,
  FileText,
  MoreHorizontal,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseConsultationUpsert,
  accessTitanversePrescriptionApproval,
} from "@/data/constantStrings";

import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";

interface PrescriptionApprovalRowProps {
  prescription: any;
  isDarkMode: boolean;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onView: () => void;
  onProcess: () => void;
  onViewConsultationSummary: () => void;
  onGoToConsultation: () => void;
  onDecline: () => void;
}

export function PrescriptionApprovalRow({
  prescription,
  isDarkMode,
  isSelected,
  onSelect,
  onView,
  onProcess,
  onViewConsultationSummary,
  onGoToConsultation,
  onDecline,
}: PrescriptionApprovalRowProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const canViewPrescription = checkUserPermissions(
    accessTitanversePrescriptionApproval,
  );
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );
  const handleMenuAction = (action: () => void) => {
    action();
    setDropdownOpen(false);
  };
  return (
    <tr className={`hover:${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
      <td className="px-3 py-4 whitespace-nowrap">
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect(!!checked)}
          aria-label={`Select prescription for ${prescription.patientName}`}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm font-medium ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {prescription.patientName}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center">
          <Pill className="w-4 h-4 text-blue-500 mr-2" />
          <div
            className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {prescription.medications.length} medication
            {prescription.medications.length === 1 ? "" : "s"}
            {prescription.medications.length > 1 && (
              <span
                className={`ml-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                (
                {prescription.medications
                  .slice(0, 2)
                  .map((med) => med.medication.split(" ")[0])
                  .join(", ")}
                {prescription.medications.length > 2 && "..."})
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {prescription.prescribedBy}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            prescription.serviceType === "pharmacy-first"
              ? "bg-blue-100 text-blue-800"
              : prescription.serviceType === "travel"
                ? "bg-purple-100 text-purple-800"
                : prescription.serviceType === "minor-ailment"
                  ? "bg-green-100 text-green-800"
                  : prescription.serviceType === "weight-management"
                    ? "bg-orange-100 text-orange-800"
                    : "bg-gray-100 text-gray-800"
          }`}
        >
          {prescription.serviceType
            .replace("-", " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {new Date(prescription.consultationDate).toLocaleDateString("en-GB")}{" "}
          at {prescription.consultationTime}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onView}
            className="h-7 px-2 text-xs"
          >
            <Eye className="w-3 h-3 mr-1" />
            View
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (!canViewPrescription) {
                setShowPermissionMessage("Approval Prescription");
                setShowPermissionModal(true);
                return;
              }
              onProcess();
            }}
            className={`h-7 px-2 text-xs ${
              canViewPrescription
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-600 opacity-50 cursor-not-allowed"
            }`}
          >
            <Settings className="w-3 h-3 mr-1" />
            Process
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!canViewPrescription) {
                setShowPermissionMessage("Approval Prescription");
                setShowPermissionModal(true);
                return;
              }
              onDecline();
            }}
            className={`h-7 px-2 text-xs border-red-300 text-red-600 ${
              canViewPrescription
                ? "hover:bg-red-50 hover:text-red-700"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <XCircle className="w-3 h-3 mr-1" />
            Decline
          </Button>

          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                aria-label="More actions"
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(!dropdownOpen);
                }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction(onView);
                }}
              >
                <Image className="mr-2 h-4 w-4" />
                View prescription image
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canViewPrescription) {
                    setShowPermissionMessage("Approval Prescription");
                    setShowPermissionModal(true);
                    return;
                  }
                  handleMenuAction(onProcess);
                }}
                className={
                  !canViewPrescription ? "opacity-50 cursor-not-allowed" : ""
                }
              >
                <Settings className="mr-2 h-4 w-4" />
                Process
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  if (!hasConsultationPermission) {
                    setShowPermissionMessage(
                      "You don't have permission to start consultation.",
                    );
                    setShowPermissionModal(true);
                    return;
                  }

                  handleMenuAction(onGoToConsultation);
                }}
              >
                <Stethoscope className="mr-2 h-4 w-4" />
                Go to Consultation
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction(onViewConsultationSummary);
                }}
              >
                <FileText className="mr-2 h-4 w-4" />
                View Consultation summary
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}
